import { useAuth } from '@clerk/react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { format, formatDistanceToNow } from 'date-fns'
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ClipboardPaste,
  Code2,
  FileText,
  Loader2,
  Play,
  RotateCcw,
  StickyNote,
  XCircle,
} from 'lucide-react'
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { TimeWindow } from '@/db/schema/sessions'
import { AUTO_SAVE_DEBOUNCE_MS } from '../../hooks/use-auto-save.js'
import { LANGUAGE_IDS } from '../../lib/test-harness/types.js'
import { trpc } from '../../lib/trpc.js'
import { cn } from '../../lib/utils.js'
import { AutoSaveTextarea } from '../ui/auto-save-textarea.js'
import { HighlightedCodeBlock } from '../ui/highlighted-code-block.js'
import { PostmortemSection } from './postmortem-section.js'
import { RestoreConfirmationDialog } from './restore-confirmation-dialog.js'
import { useSessionOptional } from './session-context.js'
import { useTestCaseInjection } from './test-case-injection-context.js'
import { stringifyValue } from './test-results/value-format'
import { useApplyCodeFromAction } from './use-apply-code-from-action.js'

// =============================================================================
// Session History Context
// =============================================================================

interface SessionHistoryContextValue {
  data: SessionHistoryResponse | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

const SessionHistoryContext = createContext<SessionHistoryContextValue | null>(
  null,
)

export function useSessionRefresh(): (() => Promise<void>) | null {
  const context = use(SessionHistoryContext)
  return context?.refresh ?? null
}

export function useSessionHistoryData(): SessionHistoryContextValue | null {
  return use(SessionHistoryContext)
}

// Provider component that sets up session history and provides data + refresh to children
export function SessionRefreshProvider({
  children,
  problemSlug,
}: {
  children: React.ReactNode
  problemSlug: string | null
}) {
  const { isSignedIn } = useAuth()
  const { data, loading, error, refresh } = useSessionHistory(
    problemSlug,
    !!isSignedIn,
  )

  // Store the latest refresh function in a ref to avoid re-triggering effects
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  // Create a stable wrapper function
  const stableRefresh = useCallback(() => {
    return refreshRef.current()
  }, [])

  const contextValue = useMemo<SessionHistoryContextValue>(
    () => ({ data, loading, error, refresh: stableRefresh }),
    [data, loading, error, stableRefresh],
  )

  return (
    <SessionHistoryContext value={contextValue}>
      {children}
    </SessionHistoryContext>
  )
}

// =============================================================================
// Types
// =============================================================================

interface ExecutionData {
  id: string
  createdAt: string
  executionMode: 'run' | 'submit' | 'draft' | 'raw'
  testsPassed: number | null
  testsTotal: number | null
  passed: boolean
  code: string
  languageId: number
  runtimeMs: number | null
  memoryKb: number | null
  notes: string | null
}

interface SessionData {
  id: string
  startedAt: string
  durationMinutes: number | null
  executionCount: number
  runCount: number
  submitCount: number
  isSolved: boolean
  executions: ExecutionData[]
  timerState?: {
    timeWindows: TimeWindow[]
    totalElapsedMs: number
  } | null
}

interface SessionHistoryResponse {
  sessions: SessionData[]
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format duration in minutes to human-readable string (for old sessions without timerState)
 */
function formatDuration(minutes: number | null): string {
  if (minutes === null) return '--'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return `${hours} hr`
  return `${hours} hr ${mins} min`
}

/**
 * Format totalElapsedMs to HH:MM:SS string
 */
function formatElapsedMs(ms: number): string {
  const totalSeconds = Math.round(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60
  return [
    String(hours).padStart(2, '0'),
    String(mins).padStart(2, '0'),
    String(secs).padStart(2, '0'),
  ].join(':')
}

/**
 * Format timestamp with relative time for recent executions (< 30 days)
 * and approximate absolute time for older executions
 */
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString)
  const now = new Date()
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  // Recent executions: show relative time
  if (date > thirtyDaysAgo) {
    return formatDistanceToNow(date, { addSuffix: true })
  }

  // Older executions: show approximate absolute time with year
  return `Around ${format(date, "h:mmaaa 'on' MMM d, yyyy")}`
}

/**
 * Get language name from Judge0 language ID
 */
function getLanguageName(languageId: number): string {
  const entry = Object.entries(LANGUAGE_IDS).find(
    ([_, id]) => id === languageId,
  )
  if (!entry) return `Language ${languageId}`

  const [lang] = entry
  return lang === 'javascript'
    ? 'JavaScript'
    : lang.charAt(0).toUpperCase() + lang.slice(1)
}

/**
 * Get the language string ('python' | 'javascript') for a Judge0 language ID.
 * Returns null for unknown IDs.
 */
function getLanguageKey(languageId: number): string | null {
  for (const [key, id] of Object.entries(LANGUAGE_IDS)) {
    if (id === languageId) return key
  }
  return null
}

// =============================================================================
// Hooks
// =============================================================================

function useSessionHistory(problemSlug: string | null, isSignedIn: boolean) {
  const {
    data: queryData,
    isLoading,
    error: queryError,
    refetch,
  } = trpc.sessions.getHistory.useQuery(
    { problemId: problemSlug! },
    { enabled: !!problemSlug && isSignedIn },
  )

  const data = queryData ?? null
  const loading = isLoading
  const error = queryError ? queryError.message : null

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  return { data, loading, error, refresh }
}

// =============================================================================
// Components
// =============================================================================

const EXECUTION_NOTE_MAX_LENGTH = 5000

function ExecutionNoteEditor({ execution }: { execution: ExecutionData }) {
  const [notes, setNotes] = useState(execution.notes ?? '')
  const saveMutation = trpc.notes.saveExecutionNote.useMutation()

  const handleSave = useCallback(
    async (value: string) => {
      await saveMutation.mutateAsync({
        executionId: execution.id,
        notes: value,
      })
    },
    [execution.id, saveMutation],
  )

  const handleChange = useCallback((value: string) => {
    if (value.length <= EXECUTION_NOTE_MAX_LENGTH) {
      setNotes(value)
    }
  }, [])

  return (
    <div>
      <AutoSaveTextarea
        value={notes}
        onChange={handleChange}
        onSave={handleSave}
        debounceMs={AUTO_SAVE_DEBOUNCE_MS.medium}
        placeholder="Add notes about this attempt..."
        rows={3}
        className="text-sm"
        maxLength={EXECUTION_NOTE_MAX_LENGTH}
      />
      <p className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
        {notes.length}/{EXECUTION_NOTE_MAX_LENGTH}
      </p>
    </div>
  )
}

interface ExecutionRowProps {
  execution: ExecutionData
  onClick: () => void
}

function getRowStatusLabel(execution: ExecutionData): {
  label: string
  color: string
} {
  if (execution.passed)
    return { label: 'Accepted', color: 'text-green-600 dark:text-green-400' }
  if (execution.testsPassed === null && execution.testsTotal === null)
    return { label: 'Error', color: 'text-red-600 dark:text-red-400' }
  return { label: 'Wrong Answer', color: 'text-red-600 dark:text-red-400' }
}

function ExecutionRow({ execution, onClick }: ExecutionRowProps) {
  const passed = execution.passed
  const isSubmit = execution.executionMode === 'submit'
  const isDraft = execution.executionMode === 'draft'
  const isRaw = execution.executionMode === 'raw'
  const hasNotes = !!execution.notes
  const status = getRowStatusLabel(execution)
  const testStats =
    execution.testsPassed !== null && execution.testsTotal !== null
      ? `${execution.testsPassed}/${execution.testsTotal} passed`
      : 'No results'

  if (isDraft) {
    return (
      <div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClick}
            className="group flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border-l-2 border-gray-400 bg-gray-500/10 px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FileText className="size-4 shrink-0 text-gray-500" />
            <span className="shrink-0 rounded-md bg-gray-500/20 px-2 py-0.5 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
              Draft
            </span>
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400">
              Code at session close
            </span>
            <span className="min-w-0 shrink text-xs text-gray-500 truncate dark:text-gray-400">
              {formatTimestamp(execution.createdAt)}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
              <Code2 className="size-4" />
              <span>View Code</span>
            </div>
          </button>

          {/* Notes button — navigates to detail view */}
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'shrink-0 rounded-md p-1.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5',
              hasNotes ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500',
            )}
            title={hasNotes ? 'View notes' : 'Add notes'}
          >
            <StickyNote className="size-4" />
          </button>
        </div>
      </div>
    )
  }

  if (isRaw) {
    // A raw ("Run Raw") execution has no test harness, so it carries no
    // pass/fail verdict. Render it neutrally instead of letting it fall into
    // the pass/fail branch below, which would show a bogus red "Error".
    return (
      <div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClick}
            className="group flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border-l-2 border-gray-400 bg-gray-500/10 px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Play className="size-4 shrink-0 text-gray-500" />
            <span className="shrink-0 rounded-md bg-gray-500/20 px-2 py-0.5 text-center text-xs font-medium text-gray-600 dark:text-gray-400">
              Raw
            </span>
            <span className="shrink-0 text-xs font-medium text-gray-600 dark:text-gray-400">
              Ran without tests
            </span>
            <span className="min-w-0 shrink text-xs text-gray-500 truncate dark:text-gray-400">
              {formatTimestamp(execution.createdAt)}
            </span>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {execution.runtimeMs !== null ? `${execution.runtimeMs}ms` : '--'}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
              <Code2 className="size-4" />
              <span>View Code</span>
            </div>
          </button>

          {/* Notes button — navigates to detail view */}
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'shrink-0 rounded-md p-1.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5',
              hasNotes ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500',
            )}
            title={hasNotes ? 'View notes' : 'Add notes'}
          >
            <StickyNote className="size-4" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'group flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border-l-2 px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5',
            passed
              ? 'border-green-500 bg-green-500/10'
              : 'border-red-500 bg-red-500/10',
          )}
        >
          {/* Icon */}
          {passed ? (
            <CheckCircle className="size-4 shrink-0 text-green-500" />
          ) : (
            <XCircle className="size-4 shrink-0 text-red-500" />
          )}

          {/* Badge */}
          <span
            className={cn(
              'shrink-0 rounded-md px-2 py-0.5 text-xs font-medium text-center',
              isSubmit
                ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                : 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
            )}
          >
            {isSubmit ? 'Submit' : 'Run'}
          </span>

          {/* Status */}
          <span className={cn('shrink-0 text-xs font-medium', status.color)}>
            {status.label}
          </span>

          {/* Test Stats */}
          <span className="shrink-0 text-gray-700 dark:text-gray-300">
            {testStats}
          </span>

          {/* Runtime */}
          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {execution.runtimeMs !== null ? `${execution.runtimeMs}ms` : '--'}
          </span>

          {/* Memory */}
          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
            {execution.memoryKb !== null
              ? `${(execution.memoryKb / 1024).toFixed(1)}MB`
              : '--'}
          </span>

          {/* Timestamp */}
          <span className="min-w-0 shrink text-xs text-gray-500 truncate dark:text-gray-400">
            {formatTimestamp(execution.createdAt)}
          </span>

          {/* View Code Indicator */}
          <div className="ml-auto flex shrink-0 items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
            <Code2 className="size-4" />
            <span>View Code</span>
          </div>
        </button>

        {/* Notes button — navigates to detail view */}
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'shrink-0 rounded-md p-1.5 transition-colors hover:bg-black/5 dark:hover:bg-white/5',
            hasNotes ? 'text-yellow-500' : 'text-gray-400 dark:text-gray-500',
          )}
          title={hasNotes ? 'View notes' : 'Add notes'}
        >
          <StickyNote className="size-4" />
        </button>
      </div>
    </div>
  )
}

function TimeWindowList({ timeWindows }: { timeWindows: TimeWindow[] }) {
  const [expanded, setExpanded] = useState(false)

  if (timeWindows.length === 0) return null

  const showCollapsed = timeWindows.length > 3 && !expanded
  const hiddenCount = timeWindows.length - 3

  const formatWindowTime = (iso: string) => {
    const date = new Date(iso)
    return (
      date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }) +
      '  ' +
      date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    )
  }

  const formatWindowDuration = (ms: number) => formatElapsedMs(ms)

  const renderWindow = (w: TimeWindow, i: number) => (
    <div key={i} className="flex justify-between text-xs text-muted-foreground">
      <span>
        {formatWindowTime(w.startedAt)} –{' '}
        {new Date(w.pausedAt).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        })}
      </span>
      <span>{formatWindowDuration(w.durationMs)}</span>
    </div>
  )

  const totalMs = timeWindows.reduce((sum, w) => sum + w.durationMs, 0)
  const totalLine = (
    <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 text-xs font-medium text-foreground dark:border-gray-600">
      <span>Total</span>
      <span>{formatElapsedMs(totalMs)}</span>
    </div>
  )

  if (showCollapsed) {
    return (
      <div className="space-y-1">
        {timeWindows.slice(0, 2).map(renderWindow)}
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full cursor-pointer border-y border-dashed border-border/50 py-1 text-center text-xs text-muted-foreground/60 hover:text-muted-foreground"
        >
          {hiddenCount} more — click to expand
        </button>
        {renderWindow(
          timeWindows[timeWindows.length - 1],
          timeWindows.length - 1,
        )}
        {totalLine}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {timeWindows.map(renderWindow)}
      {totalLine}
    </div>
  )
}

interface SessionCardProps {
  session: SessionData
  problemId: string | null
  onExecutionClick: (execution: ExecutionData) => void
  isExpanded: boolean
  onToggleExpand: () => void
  onRequestRestore: () => void
  isCurrentSession: boolean
}

function SessionCard({
  session,
  problemId,
  onExecutionClick,
  isExpanded,
  onToggleExpand,
  onRequestRestore,
  isCurrentSession,
}: SessionCardProps) {
  // For the current session, restore pulls from the most recent code
  // execution row — so it requires at least one run/submit to exist.
  const restoreDisabled = isCurrentSession && session.executions.length === 0
  const restoreTitle = restoreDisabled
    ? 'Run your code at least once to enable restore'
    : 'Restore this code into the current editor'
  // Get unique languages used in this session
  const languages = Array.from(
    new Set(session.executions.map((exec) => getLanguageName(exec.languageId))),
  ).join(', ')

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Session Header */}
      <div className="flex w-full items-center transition-colors hover:bg-black/5 dark:hover:bg-white/5">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex flex-1 items-center gap-3 p-4 text-left"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <ChevronDown className="size-4 shrink-0 text-gray-500" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-gray-500" />
          )}

          <div className="flex flex-1 items-center gap-3">
            <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
              {session.timerState?.totalElapsedMs != null
                ? formatElapsedMs(session.timerState.totalElapsedMs)
                : formatDuration(session.durationMinutes)}
            </span>

            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-xs font-medium',
                session.isSolved
                  ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                  : 'bg-gray-500/20 text-gray-600 dark:text-gray-400',
              )}
            >
              {session.isSolved ? 'Solved' : 'Unsolved'}
            </span>

            {languages && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {languages}
              </span>
            )}

            <span className="text-sm text-gray-600 dark:text-gray-400">
              {session.runCount} {session.runCount === 1 ? 'run' : 'runs'}
              {' · '}
              {session.submitCount}{' '}
              {session.submitCount === 1 ? 'submit' : 'submits'}
            </span>
          </div>
        </button>
        <Tooltip.Provider delayDuration={150}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestRestore()
                }}
                disabled={restoreDisabled}
                className="mr-3 flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-black/5 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-500 dark:text-gray-400 dark:hover:bg-white/5 dark:disabled:hover:text-gray-400"
              >
                <RotateCcw className="size-3.5" />
                Restore
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                align="center"
                className="border-border bg-background text-foreground z-50 rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
              >
                {restoreTitle}
                <Tooltip.Arrow className="fill-background" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>

      {/* Execution List */}
      {isExpanded && (
        <div className="space-y-2 border-t border-gray-200 p-4 pt-3 dark:border-gray-700">
          {session.executions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No executions yet
            </p>
          ) : (
            session.executions.map((execution) => (
              <ExecutionRow
                key={execution.id}
                execution={execution}
                onClick={() => onExecutionClick(execution)}
              />
            ))
          )}
          {session.timerState?.timeWindows &&
            session.timerState.timeWindows.length > 0 && (
              <div className="mt-3 rounded-md border border-dashed border-gray-300 p-3 dark:border-gray-600">
                <h4 className="mb-2 text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
                  Time Windows
                </h4>
                <TimeWindowList timeWindows={session.timerState.timeWindows} />
              </div>
            )}
        </div>
      )}

      {/* Postmortem Section */}
      {isExpanded && problemId && (
        <div>
          <PostmortemSection
            sessionId={session.id}
            problemId={problemId}
            sessionStartIso={session.startedAt}
            runCount={session.runCount}
            submitCount={session.submitCount}
            totalElapsedMs={session.timerState?.totalElapsedMs}
          />
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Submission Detail View
// =============================================================================

type SubmissionOutcome = 'accepted' | 'wrong-answer' | 'error' | 'ran'

function getSubmissionOutcome(detail: {
  passed: boolean
  compileOutput: string | null
  errorMessage: string | null
  testsPassed: number | null
  testsTotal: number | null
  testResults: { passed: boolean; error?: string }[] | null
}): SubmissionOutcome {
  if (detail.passed) return 'accepted'
  // Compile error or execution-level error with no test results
  if (
    detail.compileOutput ||
    (detail.errorMessage &&
      detail.testsPassed === null &&
      detail.testsTotal === null)
  )
    return 'error'
  // If the first failing test has an error (runtime error in user code),
  // classify as error rather than wrong answer
  const firstFailed = detail.testResults?.find((t) => !t.passed)
  if (firstFailed?.error) return 'error'
  return 'wrong-answer'
}

function getErrorLabel(detail: {
  compileOutput: string | null
  errorMessage: string | null
  testResults: { passed: boolean; error?: string; errorType?: string }[] | null
}): string {
  if (detail.compileOutput) return 'Compile Error'
  if (detail.errorMessage?.toLowerCase().includes('timeout'))
    return 'Time Limit Exceeded'
  // Check first failed test for a specific error type
  const firstFailed = detail.testResults?.find((t) => !t.passed)
  if (firstFailed?.errorType) {
    const et = firstFailed.errorType
    if (et.includes('Syntax') || et.includes('Indentation'))
      return 'Compile Error'
    if (et.includes('Timeout') || et.includes('TimeLimitExceeded'))
      return 'Time Limit Exceeded'
  }
  return 'Runtime Error'
}

function getLanguageForHighlighting(
  languageId: number,
): 'python' | 'javascript' {
  const entry = Object.entries(LANGUAGE_IDS).find(
    ([_, id]) => id === languageId,
  )
  return entry?.[0] === 'javascript' ? 'javascript' : 'python'
}

function formatTimeTaken(
  sessionStartedAt: string | null,
  executionCreatedAt: string,
): string {
  if (!sessionStartedAt) return '--'
  const startMs = new Date(sessionStartedAt).getTime()
  const endMs = new Date(executionCreatedAt).getTime()
  const diffMs = endMs - startMs
  if (diffMs < 0) return '--'
  const totalSeconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}m ${seconds}s`
}

interface SubmissionDetailProps {
  execution: ExecutionData
  onBack: () => void
  onRequestTransfer?: () => void
}

function getPreliminaryOutcome(execution: ExecutionData): SubmissionOutcome {
  // A raw ("Run Raw") execution runs no test harness, so it has no pass/fail
  // verdict — classify it as a neutral "ran", never the test-based "error".
  if (execution.executionMode === 'raw') return 'ran'
  if (execution.passed) return 'accepted'
  if (execution.testsPassed === null && execution.testsTotal === null)
    return 'error'
  return 'wrong-answer'
}

function SubmissionDetail({
  execution,
  onBack,
  onRequestTransfer,
}: SubmissionDetailProps) {
  const {
    data: detail,
    isLoading,
    isError,
  } = trpc.sessions.getExecutionDetail.useQuery({
    executionId: execution.id,
  })

  const injection = useTestCaseInjection()

  const languageName = getLanguageName(execution.languageId)
  const highlightLang = getLanguageForHighlighting(execution.languageId)

  // Derive outcome immediately from list data, refine with detail when available.
  // A raw execution runs no tests, so getSubmissionOutcome (which reasons only
  // about tests) would mislabel it. Classify it directly: a failed raw run
  // ('status' === 'failed', set for a compile/runtime error) still surfaces its
  // error block, while a clean raw run is a neutral "ran". Until detail loads we
  // don't know which, so show the neutral state.
  const outcome: SubmissionOutcome =
    execution.executionMode === 'raw'
      ? detail?.status === 'failed'
        ? 'error'
        : 'ran'
      : detail
        ? getSubmissionOutcome(detail)
        : getPreliminaryOutcome(execution)

  const firstFailedTest = detail?.testResults?.find((t) => !t.passed) ?? null

  const handleUseTestcase = () => {
    if (!firstFailedTest?.inputs || !injection) return
    const inputs: Record<string, string> = {}
    for (const [key, value] of Object.entries(firstFailedTest.inputs)) {
      inputs[key] = stringifyValue(value)
    }
    injection.injectTestCase({
      inputs,
      expectedOutput: stringifyValue(firstFailedTest.expected),
    })
  }

  return (
    <div className="h-full overflow-auto">
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center gap-2 border-b border-gray-200 px-4 py-2.5 text-sm text-gray-600 transition-colors hover:bg-black/5 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/5"
      >
        <ArrowLeft className="size-4" />
        All Attempts
      </button>

      <div className="mx-auto w-[80%] max-w-3xl space-y-5 py-6">
        {/* Status header */}
        <div>
          {outcome === 'accepted' && (
            <h2 className="text-xl font-semibold text-green-500">Accepted</h2>
          )}
          {outcome === 'wrong-answer' && (
            <h2 className="text-xl font-semibold text-red-500">Wrong Answer</h2>
          )}
          {outcome === 'error' && (
            <h2 className="text-xl font-semibold text-red-500">
              {detail ? getErrorLabel(detail) : 'Error'}
            </h2>
          )}
          {outcome === 'ran' && (
            <h2 className="text-xl font-semibold text-gray-500">Ran</h2>
          )}

          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {execution.executionMode === 'submit' ? 'submitted' : 'run'} on{' '}
            {format(new Date(execution.createdAt), 'MMM d, yyyy')}
          </p>

          {/* Test count */}
          {(outcome === 'accepted' ||
            outcome === 'wrong-answer' ||
            outcome === 'error') &&
            execution.testsPassed !== null &&
            execution.testsTotal !== null && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {execution.testsPassed} / {execution.testsTotal} testcases
                passed
              </p>
            )}

          {/* Metrics — for accepted */}
          {outcome === 'accepted' && (
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              {execution.runtimeMs !== null && (
                <span>Runtime: {execution.runtimeMs}ms</span>
              )}
              {execution.memoryKb !== null && (
                <span>Memory: {(execution.memoryKb / 1024).toFixed(1)}MB</span>
              )}
              {detail?.sessionStartedAt && (
                <span>
                  Time taken:{' '}
                  {formatTimeTaken(
                    detail.sessionStartedAt,
                    execution.createdAt,
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Error block */}
        {outcome === 'error' && isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="size-4 animate-spin" />
            Loading error details...
          </div>
        )}
        {outcome === 'error' && detail && (
          <>
            <div className="rounded-md bg-red-500/10 p-4">
              <pre className="border-0 whitespace-pre-wrap font-mono text-sm text-red-700 dark:text-red-400">
                {detail.compileOutput ||
                  detail.errorMessage ||
                  firstFailedTest?.error ||
                  detail.stderr}
              </pre>
            </div>

            {/* Show failing test inputs for runtime errors (like LeetCode's "Last Executed Input") */}
            {firstFailedTest?.inputs && (
              <div>
                <span className="mb-1.5 block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Last Executed Input
                </span>
                <div className="rounded-md bg-gray-100 p-3 dark:bg-gray-800">
                  {Object.entries(firstFailedTest.inputs).map(
                    ([key, value]) => (
                      <div key={key} className="font-mono text-sm">
                        <span className="text-gray-500">{key}</span>
                        <span className="text-gray-500"> = </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {stringifyValue(value)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Wrong answer — first failing test case */}
        {outcome === 'wrong-answer' && isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="size-4 animate-spin" />
            Loading test results...
          </div>
        )}
        {outcome === 'wrong-answer' && !isLoading && !firstFailedTest && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isError
              ? 'Failed to load test results.'
              : 'Test result details not available for this execution.'}
          </p>
        )}
        {outcome === 'wrong-answer' && firstFailedTest && (
          <div className="space-y-3">
            {/* Input section with Use Testcase button */}
            {firstFailedTest.inputs && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Input
                  </span>
                  {injection && (
                    <button
                      type="button"
                      onClick={handleUseTestcase}
                      className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-black/5 dark:text-gray-400 dark:hover:bg-white/5"
                    >
                      <ClipboardPaste className="size-3.5" />
                      Use Testcase
                    </button>
                  )}
                </div>
                <div className="rounded-md bg-gray-100 p-3 dark:bg-gray-800">
                  {Object.entries(firstFailedTest.inputs).map(
                    ([key, value]) => (
                      <div key={key} className="font-mono text-sm">
                        <span className="text-gray-500">{key}</span>
                        <span className="text-gray-500"> = </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {stringifyValue(value)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Output */}
            <div>
              <span className="mb-1.5 block text-sm font-medium text-gray-600 dark:text-gray-400">
                Output
              </span>
              <div className="rounded-md bg-red-500/10 p-3">
                <pre className="border-0 whitespace-pre-wrap font-mono text-sm text-red-700 dark:text-red-400">
                  {stringifyValue(firstFailedTest.actual)}
                </pre>
              </div>
            </div>

            {/* Expected */}
            <div>
              <span className="mb-1.5 block text-sm font-medium text-gray-600 dark:text-gray-400">
                Expected
              </span>
              <div className="rounded-md bg-green-500/10 p-3">
                <pre className="whitespace-pre-wrap font-mono text-sm text-green-700 dark:text-green-400">
                  {stringifyValue(firstFailedTest.expected)}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Code block */}
        <div>
          <span className="mb-1.5 block text-sm text-gray-600 dark:text-gray-400">
            Code <span className="text-gray-400 dark:text-gray-500">|</span>{' '}
            {languageName}
          </span>
          <HighlightedCodeBlock
            code={execution.code}
            language={highlightLang}
            onTransfer={onRequestTransfer}
          />
        </div>

        {/* Notes */}
        <div>
          <span className="mb-1.5 block text-sm font-medium text-gray-600 dark:text-gray-400">
            Notes
          </span>
          <ExecutionNoteEditor execution={execution} />
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Grouping Logic
// =============================================================================

interface DateGroup {
  dateLabel: string
  sessions: SessionData[]
}

interface MonthGroup {
  monthLabel: string
  dates: DateGroup[]
}

/**
 * Group sessions by month and date for organized display
 */
function groupSessionsByDate(sessions: SessionData[]): MonthGroup[] {
  // Group by month (YYYY-MM)
  const byMonth = new Map<string, Map<string, SessionData[]>>()

  for (const session of sessions) {
    const date = new Date(session.startedAt)
    const monthKey = format(date, 'yyyy-MM')
    const dateKey = format(date, 'yyyy-MM-dd')

    if (!byMonth.has(monthKey)) {
      byMonth.set(monthKey, new Map())
    }

    const monthMap = byMonth.get(monthKey)!
    if (!monthMap.has(dateKey)) {
      monthMap.set(dateKey, [])
    }

    monthMap.get(dateKey)!.push(session)
  }

  // Convert to array structure and sort
  const result: MonthGroup[] = []

  // Sort months in descending order (newest first)
  const sortedMonths = Array.from(byMonth.keys()).sort((a, b) =>
    b.localeCompare(a),
  )

  for (const monthKey of sortedMonths) {
    const datesMap = byMonth.get(monthKey)!
    // Parse as local date to avoid timezone shifts
    const [year, month] = monthKey.split('-')
    const monthDate = new Date(Number(year), Number(month) - 1, 1)
    const dates: DateGroup[] = []

    // Sort dates in descending order (newest first)
    const sortedDates = Array.from(datesMap.keys()).sort((a, b) =>
      b.localeCompare(a),
    )

    for (const dateKey of sortedDates) {
      const dateSessions = datesMap.get(dateKey)!
      // Parse as local date to avoid timezone shifts
      const [y, m, d] = dateKey.split('-')
      const date = new Date(Number(y), Number(m) - 1, Number(d))
      dates.push({
        dateLabel: format(date, 'MMMM d'),
        sessions: dateSessions,
      })
    }

    result.push({
      monthLabel: format(monthDate, 'MMMM yyyy'),
      dates,
    })
  }

  return result
}

// =============================================================================
// Main Component
// =============================================================================

export interface AttemptsPaneProps {
  problemId?: string | null
  autoExpandExecutionId?: string | null
  onRefreshReady?: (refresh: () => Promise<void>) => void
  /** Called after a successful Restore to navigate the user to the Code tab. */
  onSwitchToCode?: () => void
}

export function AttemptsPane({
  problemId,
  autoExpandExecutionId,
  onRefreshReady,
  onSwitchToCode,
}: AttemptsPaneProps = {}) {
  // Get session history data from context (provided by SessionRefreshProvider)
  const sessionHistory = useSessionHistoryData()
  const data = sessionHistory?.data ?? null
  const loading = sessionHistory?.loading ?? true
  const error = sessionHistory?.error ?? null

  const sessionContext = useSessionOptional()
  const trpcUtils = trpc.useUtils()
  const applyCode = useApplyCodeFromAction()

  const [selectedExecution, setSelectedExecution] =
    useState<ExecutionData | null>(null)
  const [expandedSessionIds, setExpandedSessionIds] = useState<Set<string>>(
    new Set(),
  )
  type PendingRestore =
    | { kind: 'session'; sessionId: string }
    | { kind: 'inline'; code: string; language: string }
  const [pendingRestore, setPendingRestore] = useState<PendingRestore | null>(
    null,
  )
  const [isRestoring, setIsRestoring] = useState(false)

  const handleRestoreConfirm = useCallback(async () => {
    if (!pendingRestore) return
    setIsRestoring(true)
    try {
      let code: string
      let language: string
      if (pendingRestore.kind === 'session') {
        const isCurrentSession =
          pendingRestore.sessionId === sessionContext?.session?.id
        const result = await trpcUtils.sessions.restoreFromSession.fetch({
          fromSessionId: pendingRestore.sessionId,
          preferExecution: isCurrentSession,
        })
        code = result.code
        language = result.language
      } else {
        code = pendingRestore.code
        language = pendingRestore.language
      }

      const result = await applyCode({
        code,
        language,
        source:
          pendingRestore.kind === 'session'
            ? 'attempt-restore'
            : 'attempt-transfer',
      })
      if (!result.ok) {
        // applyCode signals failure by return value, not by throwing. Route it
        // into the existing catch so we don't falsely mark the restore complete
        // or switch the user to the Code tab on a draft that never saved.
        throw new Error(`Failed to apply restored code: ${result.reason}`)
      }
      // Close the confirmation dialog only after the restore actually
      // succeeded. A failed fetch or apply now leaves the dialog open (with its
      // loading state cleared in `finally`) so the user can retry or cancel,
      // instead of the dialog silently vanishing on failure.
      setPendingRestore(null)
      sessionContext?.notifyRestoreCompleted()
      onSwitchToCode?.()
    } catch (err) {
      console.error('Failed to restore code:', err)
    } finally {
      setIsRestoring(false)
    }
  }, [
    pendingRestore,
    sessionContext,
    trpcUtils.sessions.restoreFromSession,
    applyCode,
    onSwitchToCode,
  ])

  // Auto-expand session containing the specified execution, collapsing all others
  useEffect(() => {
    if (autoExpandExecutionId && data) {
      // Find the session containing this execution
      const sessionWithExecution = data.sessions.find((session) =>
        session.executions.some((exec) => exec.id === autoExpandExecutionId),
      )

      if (sessionWithExecution) {
        // Reset expanded set to only the newly completed session, collapsing any
        // previously expanded sessions so the user's attention goes to the new one
        setExpandedSessionIds(new Set([sessionWithExecution.id]))
      }
    }
  }, [autoExpandExecutionId, data])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Loading sessions...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-red-500">Error: {error}</p>
      </div>
    )
  }

  if (!data || data.sessions.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No sessions yet
        </p>
      </div>
    )
  }

  const requestTransferFromExecution = (exec: ExecutionData) => {
    const language = getLanguageKey(exec.languageId)
    if (!language) {
      console.error(
        `Cannot transfer code: unknown languageId ${exec.languageId}`,
      )
      return
    }
    setPendingRestore({ kind: 'inline', code: exec.code, language })
  }

  // If a submission is selected, show its detail view
  if (selectedExecution) {
    return (
      <>
        <SubmissionDetail
          execution={selectedExecution}
          onBack={() => setSelectedExecution(null)}
          onRequestTransfer={() =>
            requestTransferFromExecution(selectedExecution)
          }
        />
        <RestoreConfirmationDialog
          open={pendingRestore !== null}
          onOpenChange={(next) => {
            if (!next) setPendingRestore(null)
          }}
          onConfirm={() => {
            void handleRestoreConfirm()
          }}
          isRestoring={isRestoring}
        />
      </>
    )
  }

  const groupedSessions = groupSessionsByDate(data.sessions)

  return (
    <div className="h-full overflow-auto p-6">
      <div className="space-y-8">
        {groupedSessions.map((monthGroup) => (
          <div key={monthGroup.monthLabel}>
            {/* Month Header */}
            <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
              {monthGroup.monthLabel}
            </h2>

            {/* Date Groups */}
            <div className="space-y-6">
              {monthGroup.dates.map((dateGroup) => (
                <div key={dateGroup.dateLabel}>
                  {/* Date Subheader */}
                  <h3 className="mb-3 text-sm font-medium text-gray-600 dark:text-gray-400">
                    {dateGroup.dateLabel}
                  </h3>

                  {/* Session Cards for this date */}
                  <div className="space-y-3">
                    {dateGroup.sessions.map((session) => (
                      <SessionCard
                        key={session.id}
                        session={session}
                        problemId={problemId ?? null}
                        onExecutionClick={setSelectedExecution}
                        isExpanded={expandedSessionIds.has(session.id)}
                        isCurrentSession={
                          session.id === sessionContext?.session?.id
                        }
                        onToggleExpand={() => {
                          setExpandedSessionIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(session.id)) {
                              next.delete(session.id)
                            } else {
                              next.add(session.id)
                            }
                            return next
                          })
                        }}
                        onRequestRestore={() =>
                          setPendingRestore({
                            kind: 'session',
                            sessionId: session.id,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <RestoreConfirmationDialog
        open={pendingRestore !== null}
        onOpenChange={(next) => {
          if (!next) setPendingRestore(null)
        }}
        onConfirm={() => {
          void handleRestoreConfirm()
        }}
        isRestoring={isRestoring}
      />
    </div>
  )
}
